import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_CONFIG from '../config/api.js';

const SubcategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    englishName: '',
    category: '',
    description: '',
    order: 0,
    isActive: true
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API_CONFIG.BASE_URL}/categories`);
      setCategories(res.data.data);
    } catch (err) {
      toast.error('Failed to fetch categories');
    }
  }, []);

  const fetchSubcategory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_CONFIG.BASE_URL}/subcategories/${id}`);
      const subcategory = res.data.data;
      
      setFormData({
        name: subcategory.name || '',
        englishName: subcategory.englishName || '',
        category: subcategory.category._id || '',
        description: subcategory.description || '',
        order: subcategory.order || 0,
        isActive: subcategory.isActive !== undefined ? subcategory.isActive : true
      });
      
      setLoading(false);
    } catch (err) {
      toast.error('Failed to fetch subcategory details');
      navigate('/subcategories');
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchCategories();
    if (isEditMode) {
      fetchSubcategory();
    } else {
      setLoading(false);
    }
  }, [isEditMode, fetchCategories, fetchSubcategory]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      };
      
      if (isEditMode) {
        await axios.put(
          `${API_CONFIG.BASE_URL}/subcategories/${id}`,
          formData,
          config
        );
        toast.success('Subcategory updated successfully');
      } else {
        await axios.post(`${API_CONFIG.BASE_URL}/subcategories`, formData, config);
        toast.success('Subcategory created successfully');
      }
      
      navigate('/subcategories');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save subcategory');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">
        {isEditMode ? 'Edit Subcategory' : 'Add Subcategory'}
      </h1>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="name" className="form-label">
                Name (नेपाली) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
              />
            </div>

            {/* English Name field removed as per requirement */}

            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="category" className="form-label">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="form-input"
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="order" className="form-label">
                Display Order
              </label>
              <input
                type="number"
                id="order"
                name="order"
                value={formData.order}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Description field removed as per requirement */}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/subcategories')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {isEditMode ? 'Update' : 'Create'} Subcategory
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubcategoryForm; 